import { useState, useRef, useEffect, useCallback } from 'react';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';
import { SearchBar } from '@/shared/components/SearchBar';
import { WrenchIcon } from '@heroicons/react/24/outline';
import { ListCard } from '@/shared/components/ListCard';
import { Snackbar } from '@/shared/components/Snackbar';
import {
  listEquipment,
  type EquipmentListItem,
} from '../../api/listEquipmentApiService';
import { EditEquipmentForm } from '../EditEquipmentForm';
import { FACILITY_LABELS } from '../../constants/facilityLabels';
import plLabels from '../../constants/plLabels.json';

const LIMIT = 10;
const DEBOUNCE_MS = 300;
const MAX_HEIGHT = 480; // px — scroll kicks in after ~4 cards

interface EquipmentListProps {
  clientId: string | null;
  facilityId: string | null;
  /** Increment this to force a refresh after new equipment is added */
  refreshKey: number;
  /** Increment this to clear any open inline edit form without re-fetching */
  resetKey?: number;
  canEdit?: boolean;
  /** SA flow: handle edit externally. CLIENT flow: omit to handle inline. */
  onEdit?: (item: EquipmentListItem) => void;
  onTotalCountChange?: (count: number) => void;
  onAccessDenied?: () => void;
  onEquipmentEdited?: () => void;
  onEditingChange?: (isDirty: boolean) => void;
  onSavingChange?: (isSaving: boolean) => void;
  onCancelEdit?: () => void;
  disabled?: boolean;
  conflictsByEquipmentId?: Record<string, string> | undefined;
}

export const EquipmentList = ({
  clientId,
  facilityId,
  refreshKey,
  resetKey,
  canEdit = false,
  onEdit,
  onTotalCountChange,
  onAccessDenied,
  onEquipmentEdited,
  onEditingChange,
  onSavingChange,
  onCancelEdit,
  disabled = false,
  conflictsByEquipmentId,
}: EquipmentListProps) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [items, setItems] = useState<EquipmentListItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<() => void>(() => {});
  const itemsRef = useRef<EquipmentListItem[]>([]);
  itemsRef.current = items;

  const [editingItem, setEditingItem] = useState<EquipmentListItem | null>(
    null
  );
  const [isEditSuccessOpen, setIsEditSuccessOpen] = useState(false);
  const [isEditErrorOpen, setIsEditErrorOpen] = useState(false);
  const [editErrorMessage, setEditErrorMessage] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset list when search or facilityId changes, or refreshKey increments
  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasNextPage(false);
    setFetchError(false);
    setEditingItem(null);
  }, [debouncedSearch, facilityId, refreshKey]);

  // Clear inline edit form without re-fetching
  useEffect(() => {
    setEditingItem(null);
  }, [resetKey]);

  useEffect(() => {
    if (!conflictsByEquipmentId) {
      return;
    }
    const conflictingId = itemsRef.current.find(
      (item) => conflictsByEquipmentId[item.equipmentId]
    )?.equipmentId;
    if (!conflictingId) {
      return;
    }
    document
      .querySelector(`[data-equipment-id="${conflictingId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [conflictsByEquipmentId]);

  // Fetch current page
  useEffect(() => {
    if (!facilityId || !clientId) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    const params = debouncedSearch
      ? { clientId, facilityId, page, limit: LIMIT, search: debouncedSearch }
      : { clientId, facilityId, page, limit: LIMIT };
    listEquipment(params)
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.accessDenied) {
          onAccessDenied?.();
          return;
        }
        if (result.success && result.data) {
          const incoming = result.data.equipment;
          setItems((prev) => (page === 1 ? incoming : [...prev, ...incoming]));
          setHasNextPage(result.data.pagination.hasNextPage);
          if (page === 1 && !debouncedSearch) {
            onTotalCountChange?.(result.data.pagination.totalCount);
          }
        } else if (!result.success) {
          setFetchError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    facilityId,
    page,
    debouncedSearch,
    refreshKey,
    onTotalCountChange,
    onAccessDenied,
  ]);

  // Infinite scroll — load next page when sentinel is visible
  const loadMore = useCallback(() => {
    if (!isLoading && hasNextPage) {
      setPage((prev) => prev + 1);
    }
  }, [isLoading, hasNextPage]);

  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreRef.current();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage]);

  const handleEditClick = (item: EquipmentListItem) => {
    if (onEdit) {
      onEdit(item);
    } else {
      setEditingItem(item);
    }
  };

  const handleEditSaved = (updated: EquipmentListItem) => {
    setItems((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item))
    );
    setEditingItem(null);
    setIsEditSuccessOpen(true);
    onEquipmentEdited?.();
  };

  // Don't render anything until a facility has been created server-side
  if (!facilityId) {
    return null;
  }

  return (
    <>
      {editingItem !== null ? (
        <EditEquipmentForm
          equipment={editingItem}
          {...(editingItem.categoryLabel && {
            initialCategoryLabel: editingItem.categoryLabel,
          })}
          onCancel={() => {
            setEditingItem(null);
            onCancelEdit?.();
            onEditingChange?.(false);
          }}
          onSaved={(updated) => {
            handleEditSaved(updated);
            setIsEditSuccessOpen(true);
            onEditingChange?.(false);
          }}
          onError={(message) => {
            setEditErrorMessage(message);
            setIsEditErrorOpen(true);
          }}
          onDirtyChange={(isDirty) => onEditingChange?.(isDirty)}
          {...(onSavingChange && { onSavingChange })}
        />
      ) : (
        <div
          className={`flex flex-col gap-4${disabled ? ' pointer-events-none opacity-50' : ''}`}
        >
          {/* Search bar */}
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Szukaj po nazwie"
            inputTestId="equipment-search-bar-input"
          />

          {/* Skeleton — page 1 loading */}
          {isLoading && items.length === 0 && (
            <div className="flex flex-col gap-4">
              {Array.from({ length: 1 }).map((_, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-[#DCDDE0] rounded-[12px] p-4 flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse shrink-0" />
                  <div className="flex flex-col gap-2 flex-1 min-w-0">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                  </div>
                  <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse shrink-0" />
                </div>
              ))}
            </div>
          )}

          {/* Equipment cards */}
          {items.length > 0 && (
            <div
              className="flex flex-col gap-4 overflow-y-auto pr-1"
              style={{ maxHeight: MAX_HEIGHT }}
            >
              {items.map((item, i) => (
                <div key={item.id} data-equipment-id={item.equipmentId}>
                  <ListCard
                    name={item.name}
                    entityId={item.equipmentId}
                    imageUrl={item.image?.url ?? null}
                    icon={
                      <WrenchIcon className="w-6 h-6 text-on-surface-variant" />
                    }
                    index={i}
                    editDisabled={!canEdit}
                    editDisabledTooltip={
                      !canEdit ? plLabels.common.noPermission : undefined
                    }
                    onEdit={() => handleEditClick(item)}
                    errorMessage={conflictsByEquipmentId?.[item.equipmentId]}
                  />
                </div>
              ))}
              {hasNextPage && (
                <div ref={sentinelRef} className="h-1 shrink-0" />
              )}
              {isLoading && items.length > 0 && (
                <div className="bg-white border border-[#DCDDE0] rounded-[12px] p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse shrink-0" />
                  <div className="flex flex-col gap-2 flex-1 min-w-0">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                  </div>
                  <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse shrink-0" />
                </div>
              )}
            </div>
          )}

          {!isLoading && items.length === 0 && debouncedSearch && (
            <div className="flex flex-col items-center w-full gap-[22px]">
              <LottieAnimation
                animationData={noResultsAnimation}
                width={240}
                aspectRatio="240 / 194"
              />
              <div className="flex flex-col items-center gap-1 w-full">
                <p className="w-full text-center text-[#474A4C] text-sm font-medium leading-5 tracking-[0.25px]">
                  Nie znaleziono wyników.
                </p>
                <p className="w-full text-center text-[#474A4C] text-xs font-normal leading-4 tracking-[0.4px]">
                  Spróbuj zmienić kryteria wyszukiwania.
                </p>
              </div>
            </div>
          )}

          {!isLoading &&
            items.length === 0 &&
            !debouncedSearch &&
            !fetchError && (
              <div className="flex flex-col items-center justify-center gap-2 py-6">
                <WrenchIcon className="w-8 h-8 text-outline" />
                <p className="text-sm text-outline">Brak aparatury</p>
              </div>
            )}

          {!isLoading && fetchError && (
            <p className="text-center text-sm text-error py-4">
              Nie udało się załadować listy aparatury.
            </p>
          )}
        </div>
      )}

      <Snackbar
        open={isEditSuccessOpen}
        message={FACILITY_LABELS.EDIT_EQUIPMENT_SUCCESS}
        variant="success"
        onClose={() => setIsEditSuccessOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={isEditErrorOpen}
        message={editErrorMessage}
        variant="error"
        onClose={() => setIsEditErrorOpen(false)}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />
    </>
  );
};
